if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.viewerButtons = {
          previous: this.elements.viewer?.querySelector('button[name="previous"]'),
          next: this.elements.viewer?.querySelector('button[name="next"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initViewerNavigation();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initViewerNavigation() {
        if (!this.viewerButtons.previous || !this.viewerButtons.next) return;

        this.viewerButtons.previous.addEventListener('click', this.onViewerButtonClick.bind(this, -1), true);
        this.viewerButtons.next.addEventListener('click', this.onViewerButtonClick.bind(this, 1), true);
        this.updateViewerNavigationState();
      }

      onViewerButtonClick(step, event) {
        const mediaItems = this.getViewerMediaItems();
        if (mediaItems.length < 2) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const currentIndex = this.getCurrentMediaIndex(mediaItems);
        const nextIndex = (currentIndex + step + mediaItems.length) % mediaItems.length;

        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      getViewerMediaSelector() {
        return '[id^="Slide-"][data-media-id]';
      }

      getViewerMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll(this.getViewerMediaSelector()));
      }

      getCurrentMediaIndex(mediaItems = this.getViewerMediaItems()) {
        if (!mediaItems.length) return -1;

        const activeMedia =
          this.elements.viewer?.querySelector('[data-media-id].is-active') ||
          this.elements.viewer?.querySelector('[data-media-id]');
        const activeIndex = mediaItems.findIndex((element) => element.dataset.mediaId === activeMedia?.dataset.mediaId);

        return activeIndex === -1 ? 0 : activeIndex;
      }

      updateViewerNavigationState() {
        if (!this.viewerButtons.previous || !this.viewerButtons.next) return;

        const mediaItems = this.getViewerMediaItems();
        const shouldDisable = mediaItems.length < 2;

        this.viewerButtons.previous.toggleAttribute('disabled', shouldDisable);
        this.viewerButtons.next.toggleAttribute('disabled', shouldDisable);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateViewerNavigationState();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`${this.getViewerMediaSelector()}[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector(this.getViewerMediaSelector());
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll(this.getViewerMediaSelector()).forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateViewerNavigationState();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
